import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminDeclaredLoadDashboard, fetchAdminDeclaredLoads } from '../services/api/admin';
import type {
  AdminDeclaredLoadDashboard,
  AdminDeclaredLoadDriverRow,
  AdminDeclaredLoadEligibility,
  AdminDeclaredLoadListItem,
} from '../types/admin';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATUS_OPTIONS = [
  'ACCEPTED',
  'ON_ROUTE_TO_PICKUP',
  'LOADING',
  'PICKED_UP',
  'ENROUTE',
  'UNLOADING',
  'DELIVERED',
  'COMPLETED',
  'CANCELLED',
  'CANCELLED_BY_SYSTEM',
  'EXPIRED',
  'REJECTED',
];

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultFromDate(): string {
  const now = new Date();
  return formatDateForInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

function defaultToDate(): string {
  return formatDateForInput(new Date());
}

function formatCompactNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA').format(Number(value ?? 0));
}

function formatPercent(value: number | null | undefined): string {
  return `${new Intl.NumberFormat('en-ZA', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number(value ?? 0))}%`;
}

function normalizeLabel(value: string | null | undefined): string {
  return (value || 'UNKNOWN')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function eligibilityClass(status: string | null | undefined): string {
  if (status === 'ELIGIBLE') return 'admin-pill-success';
  if (status === 'REVIEW') return 'admin-pill-warning';
  return '';
}

function statusClass(status: string | null | undefined): string {
  if (status === 'COMPLETED' || status === 'DELIVERED') return 'admin-pill-success';
  if (['ACCEPTED', 'ON_ROUTE_TO_PICKUP', 'LOADING', 'PICKED_UP', 'ENROUTE', 'UNLOADING'].includes(status || '')) {
    return 'admin-pill-active';
  }
  if (['CANCELLED', 'CANCELLED_BY_SYSTEM', 'REJECTED'].includes(status || '')) return 'admin-pill-danger';
  return 'admin-pill-warning';
}

function customerTypeLabel(value: boolean | null | undefined): string {
  if (value === true) return 'New customer';
  if (value === false) return 'Existing customer';
  return 'Customer audit pending';
}

function progressLabel(load: AdminDeclaredLoadListItem): string {
  if (load.completedAt) return 'Completed';
  if (load.deliveredAt) return 'Delivered';
  if (load.enrouteAt) return 'En route';
  if (load.pickedUpAt) return 'Picked up';
  if (load.onRouteToPickupAt) return 'To pickup';
  if (load.acceptedAt) return 'Accepted';
  if (load.cancelledAt) return 'Cancelled';
  return 'No progress';
}

function trackingLabel(load: AdminDeclaredLoadListItem): string {
  if (!load.trackingLinkOpened) return 'Not opened yet';
  const count = formatCompactNumber(load.trackingLinkOpenCount);
  return `${count} ${load.trackingLinkOpenCount === 1 ? 'open' : 'opens'}`;
}

const AdminDeclaredLoadsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState<AdminDeclaredLoadDashboard | null>(null);
  const [loads, setLoads] = useState<AdminDeclaredLoadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const filters = useMemo(
    () => ({
      fromDate: searchParams.get('fromDate') || defaultFromDate(),
      toDate: searchParams.get('toDate') || defaultToDate(),
      driverId: searchParams.get('driverId') || '',
      eligibility: (searchParams.get('eligibility') || '') as AdminDeclaredLoadEligibility | '',
      customerCreated: searchParams.get('customerCreated') || '',
      status: searchParams.get('status') || '',
      paymentStatus: searchParams.get('paymentStatus') || '',
      query: searchParams.get('query') || '',
      page: Number(searchParams.get('page') || '0'),
      size: Number(searchParams.get('size') || '12'),
      sort: searchParams.get('sort') || 'declaredAt,desc',
    }),
    [searchParams],
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!next.get('fromDate')) {
      next.set('fromDate', filters.fromDate);
      changed = true;
    }
    if (!next.get('toDate')) {
      next.set('toDate', filters.toDate);
      changed = true;
    }
    if (changed) setSearchParams(next);
  }, [filters.fromDate, filters.toDate, searchParams, setSearchParams]);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const [dashboardResponse, loadsResponse] = await Promise.all([
        fetchAdminDeclaredLoadDashboard({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          ...(filters.driverId ? { driverId: filters.driverId.trim() } : {}),
          ...(filters.eligibility ? { eligibility: filters.eligibility } : {}),
          limit: 100,
        }),
        fetchAdminDeclaredLoads({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          ...(filters.driverId ? { driverId: filters.driverId.trim() } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.customerCreated ? { customerCreated: filters.customerCreated } : {}),
          ...(filters.paymentStatus ? { paymentStatus: filters.paymentStatus } : {}),
          ...(filters.query ? { query: filters.query.trim() } : {}),
          page: filters.page,
          size: filters.size,
          sort: filters.sort,
        }),
      ]);
      setDashboard(dashboardResponse);
      setLoads(loadsResponse.items);
      setTotalPages(loadsResponse.totalPages);
      setTotalElements(loadsResponse.totalElements);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load declared loads.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set('page', '0');
    setSearchParams(next);
  };

  const updatePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
  };

  const selectDriver = (driver: AdminDeclaredLoadDriverRow) => {
    updateFilter('driverId', driver.driverId);
  };

  const summary = dashboard?.summary;

  return (
    <AdminShell
      badge="Declared Loads"
      copy="Review loads drivers brought in from outside ShiftWagon, track customer conversion, and audit incentive-worthy delivery activity."
      title="Declared-load dashboard"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <section className="admin-panel">
        <div className="admin-toolbar admin-toolbar-declared-loads">
          <label className="admin-filter-field">
            <span>From</span>
            <input className="register-input" onChange={(event) => updateFilter('fromDate', event.target.value)} type="date" value={filters.fromDate} />
          </label>
          <label className="admin-filter-field">
            <span>To</span>
            <input className="register-input" onChange={(event) => updateFilter('toDate', event.target.value)} type="date" value={filters.toDate} />
          </label>
          <label className="admin-filter-field">
            <span>Eligibility</span>
            <select className="register-input" onChange={(event) => updateFilter('eligibility', event.target.value)} value={filters.eligibility}>
              <option value="">All drivers</option>
              <option value="ELIGIBLE">Eligible</option>
              <option value="REVIEW">Review</option>
              <option value="NOT_ELIGIBLE">Not eligible</option>
            </select>
          </label>
          <label className="admin-filter-field">
            <span>Customer</span>
            <select className="register-input" onChange={(event) => updateFilter('customerCreated', event.target.value)} value={filters.customerCreated}>
              <option value="">All customers</option>
              <option value="true">New</option>
              <option value="false">Existing</option>
            </select>
          </label>
          <label className="admin-filter-field">
            <span>Status</span>
            <select className="register-input" onChange={(event) => updateFilter('status', event.target.value)} value={filters.status}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {normalizeLabel(status)}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-filter-field admin-toolbar-wide">
            <span>Search</span>
            <input
              className="register-input"
              onChange={(event) => updateFilter('query', event.target.value)}
              placeholder="Booking, customer, driver, route"
              value={filters.query}
            />
          </label>
          <button className="register-secondary-button" disabled={refreshing || loading} onClick={() => void loadData(true)} type="button">
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </section>

      <div className="admin-grid admin-grid-cards" style={{ marginTop: '18px' }}>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Declared loads</span>
          <strong>{formatCompactNumber(summary?.declaredLoads)}</strong>
          <span className="admin-stat-note">Driver-declared bookings in the selected window.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Completed</span>
          <strong>{formatCompactNumber(summary?.completedLoads)}</strong>
          <span className="admin-stat-note">{formatPercent(summary?.completionRate)} completion rate.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Active</span>
          <strong>{formatCompactNumber(summary?.activeLoads)}</strong>
          <span className="admin-stat-note">Accepted or currently moving loads.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Needs review</span>
          <strong>{formatCompactNumber((summary?.cancelledLoads ?? 0) + (summary?.noProgressLoads ?? 0))}</strong>
          <span className="admin-stat-note">Cancelled, failed, or accepted without movement.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Tracking opened</span>
          <strong>{formatCompactNumber(summary?.trackingLinksOpened)}</strong>
          <span className="admin-stat-note">
            {formatCompactNumber(summary?.totalTrackingLinkOpens)} total opens · {formatPercent(summary?.trackingOpenRate)} open rate.
          </span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">New customers</span>
          <strong>{formatCompactNumber(summary?.newCustomersCreated)}</strong>
          <span className="admin-stat-note">{formatPercent(summary?.newCustomerRate)} of declared loads.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Customers reached</span>
          <strong>{formatCompactNumber(summary?.uniqueDeclaredCustomers)}</strong>
          <span className="admin-stat-note">{formatCompactNumber(summary?.existingCustomersReused)} existing accounts reused.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Gross declared fare</span>
          <strong>{formatCurrency(summary?.grossDeclaredFare)}</strong>
          <span className="admin-stat-note">Final fare basis across declarations.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Payout estimate</span>
          <strong>{formatCurrency(summary?.estimatedDriverPayout)}</strong>
          <span className="admin-stat-note">Known driver payout values from booking rows.</span>
        </div>
      </div>

      {dashboard?.warnings.length ? (
        <section className="admin-panel" style={{ marginTop: '18px' }}>
          <div className="admin-pill-row">
            {dashboard.warnings.map((warning) => (
              <span className="admin-pill admin-pill-warning" key={warning}>
                {warning}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section className="admin-panel" style={{ marginTop: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Driver ranking</h2>
            <p className="admin-panel-copy">Click a driver row to filter the load audit table to that driver.</p>
          </div>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <div className="admin-table">
            <div className="admin-table-head admin-table-head-declared-drivers">
              <span>Driver</span>
              <span>Loads</span>
              <span>Customers</span>
              <span>Tracking</span>
              <span>Money</span>
              <span>Eligibility</span>
              <span>Last declared</span>
            </div>
            {(dashboard?.driverRows ?? []).map((driver) => (
              <button className="admin-table-row admin-table-row-declared-drivers admin-table-row-button" key={driver.driverId} onClick={() => selectDriver(driver)} type="button">
                <span>
                  <strong>{driver.driverName || driver.driverId}</strong>
                  <small>{driver.driverPhone || 'Phone unavailable'}</small>
                  <small>{driver.driverEmail || 'Email unavailable'}</small>
                </span>
                <span>
                  <strong>{formatCompactNumber(driver.declaredLoads)} declared</strong>
                  <small>{formatCompactNumber(driver.completedLoads)} completed</small>
                  <small>{formatCompactNumber(driver.noProgressLoads)} no progress</small>
                </span>
                <span>
                  <strong>{formatCompactNumber(driver.uniqueDeclaredCustomers)} unique</strong>
                  <small>{formatCompactNumber(driver.newCustomersCreated)} new</small>
                  <small>{formatCompactNumber(driver.existingCustomersReused)} existing</small>
                </span>
                <span>
                  <strong>{formatCompactNumber(driver.trackingLinksOpened)} opened</strong>
                  <small>{formatCompactNumber(driver.totalTrackingLinkOpens)} total opens</small>
                  <small>{formatPercent(driver.trackingOpenRate)} open rate</small>
                </span>
                <span>
                  <strong>{formatCurrency(driver.grossDeclaredFare)}</strong>
                  <small>Payout {formatCurrency(driver.estimatedDriverPayout)}</small>
                  <small>Agreed {formatCurrency(driver.agreedFareTotal)}</small>
                </span>
                <span>
                  <span className={`admin-pill ${eligibilityClass(driver.eligibilityStatus)}`}>
                    {normalizeLabel(driver.eligibilityStatus)}
                  </span>
                  {driver.reviewFlags.slice(0, 2).map((flag) => (
                    <small key={flag}>{normalizeLabel(flag)}</small>
                  ))}
                </span>
                <span>
                  <strong>{formatDateTime(driver.lastDeclaredAt)}</strong>
                  <small>First: {formatDateTime(driver.firstDeclaredAt)}</small>
                  <small>Tracking: {formatDateTime(driver.lastTrackingOpenedAt)}</small>
                </span>
              </button>
            ))}
            {!dashboard?.driverRows.length ? <p className="admin-empty">No drivers match the current declared-load filters.</p> : null}
          </div>
        )}
      </section>

      <section className="admin-panel" style={{ marginTop: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Declared-load audit</h2>
            <p className="admin-panel-copy">Each row is a booking declared by a driver outside the normal customer schedule flow.</p>
          </div>
          {filters.driverId ? (
            <button className="register-secondary-button" onClick={() => updateFilter('driverId', '')} type="button">
              Clear driver
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table">
              <div className="admin-table-head admin-table-head-declared-loads">
                <span>Booking</span>
                <span>Driver</span>
                <span>Customer</span>
                <span>Route</span>
                <span>Status</span>
                <span>Tracking</span>
                <span>Money</span>
                <span>Progress</span>
              </div>
              {loads.map((load) => (
                <div className="admin-table-row admin-table-row-declared-loads" key={`${load.bookingId}-${load.tripId}`}>
                  <span>
                    <Link className="admin-row-link" to={`/admin/trips/${load.tripId}`}>
                      {load.bookingId || 'Booking pending'}
                    </Link>
                    <small>{formatDateTime(load.declaredAt)}</small>
                    <small>{load.vehicleType || 'Vehicle pending'}</small>
                  </span>
                  <span>
                    <strong>{load.driverName || load.declaredByDriverId || 'Driver unavailable'}</strong>
                    <small>{load.driverPhone || 'Phone unavailable'}</small>
                    <small>{load.driverEmail || 'Email unavailable'}</small>
                  </span>
                  <span>
                    <strong>{load.declaredCustomerName || 'Customer'}</strong>
                    <small>{load.declaredCustomerEmail || 'Email unavailable'}</small>
                    <small>{customerTypeLabel(load.declaredCustomerCreated)}</small>
                  </span>
                  <span>
                    <strong>{load.sourceAddress || 'Pickup pending'}</strong>
                    <small>{load.destinationAddress || 'Destination pending'}</small>
                  </span>
                  <span>
                    <span className={`admin-pill ${statusClass(load.status)}`}>{normalizeLabel(load.status)}</span>
                    <small>{load.paymentStatus || 'Payment pending'}</small>
                  </span>
                  <span>
                    <strong>{trackingLabel(load)}</strong>
                    <small>{load.trackingLinkLastOpenedAt ? `Last ${formatDateTime(load.trackingLinkLastOpenedAt)}` : 'Awaiting first open'}</small>
                  </span>
                  <span>
                    <strong>{formatCurrency(load.fareAmount ?? load.agreedFare ?? load.estimatedFare)}</strong>
                    <small>Estimate {formatCurrency(load.estimatedFare)}</small>
                    <small>{normalizeLabel(load.fareSource)}</small>
                  </span>
                  <span>
                    <strong>{progressLabel(load)}</strong>
                    <small>Scheduled {formatDateTime(load.scheduledAt)}</small>
                    {load.cancelReason ? <small>{load.cancelReason}</small> : null}
                  </span>
                </div>
              ))}
              {!loads.length ? <p className="admin-empty">No declared loads match the current filters.</p> : null}
            </div>

            <div className="admin-pagination">
              <span>{formatCompactNumber(totalElements)} declared loads</span>
              <button className="register-secondary-button" disabled={filters.page === 0} onClick={() => updatePage(Math.max(filters.page - 1, 0))} type="button">
                Previous
              </button>
              <span>
                Page {filters.page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                className="register-secondary-button"
                disabled={filters.page + 1 >= Math.max(totalPages, 1)}
                onClick={() => updatePage(filters.page + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
    </AdminShell>
  );
};

export default AdminDeclaredLoadsPage;
